import {
  PERMISSION_BY_KEY,
  PERMISSION_WHITE_LIST
} from '@/constants/permissions.constant';
import { PermissionMode } from '@/enums/mode.enum';
import { PermissionField } from '@/enums/permission.enum';
import { AccessPermissionContext } from '@/hooks/access-permission.hook';
import usePermission from '@/hooks/permission.hook';
import { useQuery } from '@/hooks/query.hook';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  getProfileUser,
  watchLoggedIn,
  watchUser
} from '@/redux/slices/auth.slice';
import { fetchUserModules, watchModules } from '@/redux/slices/module.slice';
import { PageURLs } from '@/utils/navigate';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export const AccessPermissionProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(watchLoggedIn);
  const user = useAppSelector(watchUser);
  const modules = useAppSelector(watchModules);

  const { pathname } = useLocation();

  const { mode } = useQuery();

  const { getPermissions, getAllowedPermissions } = usePermission();

  const [showChildren, setShowChildren] = useState(true);
  const [roleIndexes, setRoleIndexes] = useState<string[] | undefined>();

  const roleSettings = useCallback(
    async (forceUpdate?: boolean) => {
      if (user) {
        if (!user.role) {
          dispatch(getProfileUser());
        } else {
          if (!roleIndexes || forceUpdate) {
            setShowChildren(false);
            const getDetailRoles = user.role.map((item) =>
              apiFetcher
                .post(`role/detail`, {
                  roleId: item.id
                })
                .then((it) => it.data)
            );

            const result = await Promise.all(getDetailRoles);

            const roleIndexes = result.map((item: RoleDetail) => {
              return item.modulePermission.map((ml) => {
                const { parentModuleIndex, moduleIndex, permissionIndex } = ml;

                return `${parentModuleIndex}-${moduleIndex}-${permissionIndex}`;
              });
            });

            const roleIndexesFlat = [...new Set(roleIndexes.flat())];

            setRoleIndexes(roleIndexesFlat);
            setShowChildren(true);
          }
        }
      }
    },
    [dispatch, roleIndexes, user]
  );

  useEffect(() => {
    if (user) {
      roleSettings(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchUserModules());
    }
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    if (roleIndexes) {
      const roles = roleIndexes
        .map(
          (item) => PERMISSION_BY_KEY[item as keyof typeof PERMISSION_BY_KEY]
        )
        .filter((item) => item !== undefined);

      if (![...roles, ...PERMISSION_WHITE_LIST].includes(pathname)) {
        navigate(PageURLs.of403());
      }
    }
  }, [navigate, pathname, roleIndexes]);

  useEffect(() => {
    if (roleIndexes) {
      const allowedPermissions = getAllowedPermissions(roleIndexes);
      const permissions = getPermissions(allowedPermissions);

      if (
        (mode === PermissionMode.CREATE &&
          !permissions[PermissionField.CREATE]) ||
        (mode === PermissionMode.EDIT && !permissions[PermissionField.UPDATE])
      ) {
        navigate(PageURLs.of403());
      }
    }
  }, [getAllowedPermissions, getPermissions, mode, navigate, roleIndexes]);

  return (
    <AccessPermissionContext.Provider
      value={{ modules, roleIndexes, roleSettings }}
    >
      {showChildren && children}
    </AccessPermissionContext.Provider>
  );
};